/**
 * MissingCoverPlaceholder — Phase 5 Slice C (2026-07-28).
 *
 * Rendered in place of a hero/cover photo when `cover_image_url` is
 * `null` (typical for dream vacations / chapters / stories / booked
 * trips whose cover pointer was nulled by the Google Places wipe).
 *
 * Design: solid navy gradient (never cream/gold), white centered
 * ImagePlus icon at ~40px, "Add cover photo" label below. Fills the
 * parent — set `aspect-ratio` / `w-full h-full` on the wrapper you
 * mount it inside.
 *
 * Clickable: passes through `onClick` — parent wires the upload flow.
 * If `onClick` is omitted, the placeholder renders as a static tile
 * (no click affordance, no cursor change).
 */
import React from "react";
import { ImagePlus } from "lucide-react";

export default function MissingCoverPlaceholder({
  onClick,
  label = "Add cover photo",
  className = "",
  size = "md", // sm | md | lg
  testId = "missing-cover-placeholder",
}) {
  const iconSize = size === "sm" ? "w-6 h-6" : size === "lg" ? "w-14 h-14" : "w-10 h-10";
  const labelSize = size === "sm" ? "text-[10px]" : size === "lg" ? "text-base" : "text-xs";
  const Element = onClick ? "button" : "div";
  return (
    <Element
      type={onClick ? "button" : undefined}
      onClick={onClick}
      data-testid={testId}
      aria-label={onClick ? label : undefined}
      className={
        "w-full h-full flex flex-col items-center justify-center gap-2 " +
        "text-white/90 border-none " +
        (onClick
          ? "cursor-pointer hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-tb-gold/70 tb-tap "
          : "cursor-default ") +
        className
      }
      style={{
        background:
          "linear-gradient(135deg, rgba(26,58,95,0.85) 0%, rgba(20,44,74,1) 100%)",
      }}
    >
      <ImagePlus className={iconSize} strokeWidth={1.5} />
      <span className={"font-hand tracking-wide uppercase " + labelSize}>
        {label}
      </span>
    </Element>
  );
}
